<script setup lang="ts">
import { useRouter, useRoute, RouterView } from 'vue-router'
import { computed } from 'vue'
import Menubar from 'primevue/menubar'
import Button from 'primevue/button'
import Toast from 'primevue/toast'
import ConfirmDialog from 'primevue/confirmdialog'
import { useBoekhouding } from '@/composables/useBoekhouding'
import { useImportExport } from '@/composables/useImportExport'
import { useAutosave } from '@/composables/useAutosave'

useRouter()
const route = useRoute()
const { fileHandle } = useBoekhouding()
const { openFile, createNewFile } = useImportExport()
const { isSaving, lastSavedLabel } = useAutosave()

const menuItems = [
  { label: 'Dashboard', icon: 'pi pi-home', to: '/' },
  { label: 'Dagboek', icon: 'pi pi-list', to: '/dagboek' },
  { label: 'Snel boeken', icon: 'pi pi-bolt', to: '/snelboeking' },
  { label: 'Bijdrage boeken', icon: 'pi pi-plus-circle', to: '/bijdrage' },
  { label: 'Overzichten', icon: 'pi pi-chart-bar', to: '/overzichten' },
  { label: 'Instellingen', icon: 'pi pi-cog', to: '/instellingen' },
]

const containerClass = computed(() =>
  route.path === '/dagboek' ? 'max-w-none mx-4' : 'container mx-auto'
)

async function handleOpen() {
  try { await openFile() } catch (e: any) { if (e?.name !== 'AbortError') throw e }
}

async function handleNew() {
  try { await createNewFile() } catch (e: any) { if (e?.name !== 'AbortError') throw e }
}
</script>

<template>
  <!-- Open screen: no file loaded yet -->
  <div v-if="!fileHandle" class="min-h-screen flex flex-col items-center justify-center gap-6">
    <h1 class="text-3xl font-bold">Boekhouding</h1>
    <p class="text-gray-500">Kies een bestand om te beginnen.</p>
    <div class="flex gap-3">
      <Button label="Open bestaand bestand" icon="pi pi-folder-open" @click="handleOpen" />
      <Button label="Nieuw bestand aanmaken" icon="pi pi-file" severity="secondary" @click="handleNew" />
    </div>
  </div>

  <!-- Normal app layout: file is open -->
  <template v-else>
    <header class="bg-white border-b border-gray-200">
      <Menubar :model="menuItems" class="border-none px-4 py-2">
        <template #item="{ item, props }">
          <router-link v-if="item.to" :to="item.to" v-slot="{ href, navigate }" custom>
            <a :href="href" v-bind="props.action" @click="navigate">
              <span :class="item.icon" />
              <span class="ml-2">{{ item.label }}</span>
            </a>
          </router-link>
          <a v-else v-bind="props.action">
            <span :class="item.icon" />
            <span class="ml-2">{{ item.label }}</span>
          </a>
        </template>
        <template #end>
          <span v-if="isSaving" class="text-sm text-gray-400 flex items-center gap-1">
            <i class="pi pi-spin pi-spinner" /> Opslaan...
          </span>
          <span v-else-if="lastSavedLabel()" class="text-sm text-gray-400">
            <i class="pi pi-check" /> {{ lastSavedLabel() }}
          </span>
        </template>
      </Menubar>
    </header>
    <main :class="[containerClass, 'p-4 md:p-8']">
      <RouterView />
    </main>
  </template>

  <Toast />
  <ConfirmDialog />
</template>

<style>
/* Global layout tweaks */
body {
  margin: 0;
  padding: 0;
}
</style>
